import './index.css';
import './i18n'; // Initialize i18n
import NavBar from './components/blocks/nav';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import DBTable from './handlers/DatabaseHandler';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Footer from './components/blocks/Footer';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage, loadLanguageFromProfile } from './utils/languageUtils';
import { useLanguageSync } from './hooks/useLanguageSync';
import useScrollToTop from './hooks/useScrollToTop';

import Home from './pages/Home';
import NotFound from './pages/NotFound';
import Titan from './pages/titan/Titan';
import Cortex from './pages/cortex/Cortex';
import Jeltr from './pages/jeltr/Jeltr';
import Archives from './pages/Archives';
import ContactUs from './pages/ContactUs';
import { EVENT_ROUTES } from './config/eventIndex';


// Scroll to top component for route changes
const ScrollToTop = () => {
  // Use the custom hook with improved configuration
  useScrollToTop({
    enabled: true,
    behavior: 'auto',
    delay: 10, // Small delay to ensure DOM is ready
    excludePaths: [], // No excluded paths
    retryAttempts: 3, // Retry up to 3 times if scroll fails
    retryDelay: 100 // Wait 100ms between retries
  });

  return null;
};

// Language initialization component
const LanguageInitializer = ({ children }) => {
  const { i18n } = useTranslation();
  const { isAuthenticated, currentUser, loading } = useAuth();
  const [, setIsLanguageReady] = useState(false);
  const [hasReloaded, setHasReloaded] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        // Don't run if already completed
        if (hasCompleted) {
          return;
        }

        // Wait for authentication context to be ready
        if (loading) {
          return;
        }

        // Check if this is the first load or if language needs to be synchronized
        const isFirstLoad = !localStorage.getItem('languageInitialized');
        const currentLanguage = i18n.language || 'en';

        if (isFirstLoad && !hasReloaded) {
          // Mark that we've attempted initialization
          setHasReloaded(true);

          // Wait for i18n to be ready
          if (!i18n.isInitialized) {
            await new Promise(resolve => {
              const checkInitialized = () => {
                if (i18n.isInitialized) {
                  resolve();
                } else {
                  setTimeout(checkInitialized, 100);
                }
              };
              checkInitialized();
            });
          }

          // Load user language preference if authenticated
          let targetLanguage = 'en';
          if (isAuthenticated && currentUser?.User_ID) {
            try {
              const userTable = new DBTable("USER", "User_ID", {});
              const userData = await userTable.handleRead({ User_ID: currentUser.User_ID }, false);
              targetLanguage = await loadLanguageFromProfile(userData);
            } catch (error) {
              console.error('Error loading user language preference:', error);
              targetLanguage = getCurrentLanguage();
            }
          } else {
            // For non-authenticated users, check localStorage first, then browser language
            const savedLanguage = localStorage.getItem('i18nextLng');
            if (savedLanguage && ['en', 'tc', 'sc'].includes(savedLanguage)) {
              targetLanguage = savedLanguage;
            } else {
              // Check browser language
              const browserLang = navigator.language || navigator.userLanguage;
              if (browserLang.startsWith('zh')) {
                // Determine if it's simplified or traditional Chinese
                if (browserLang.includes('CN') || browserLang.includes('Hans')) {
                  targetLanguage = 'sc';
                } else {
                  targetLanguage = 'tc';
                }
              } else {
                targetLanguage = 'en';
              }
            }
          }

          // If the current language doesn't match the target language, reload
          if (currentLanguage !== targetLanguage) {
            await i18n.changeLanguage(targetLanguage);
            // Force a page reload to ensure all components render with the correct language
            window.location.reload();
            return;
          } else {
            // Languages match, mark as completed
            localStorage.setItem('languageInitialized', 'true');
            setHasCompleted(true);
            setIsLanguageReady(true);
            return;
          }
        } else {
          // Not first load, mark as completed
          setHasCompleted(true);
          setIsLanguageReady(true);
        }

        // Set language as ready
        setIsLanguageReady(true);
      } catch (error) {
        console.error('Error during language initialization:', error);
        // Set as ready even if there's an error to prevent infinite loading
        setHasCompleted(true);
        setIsLanguageReady(true);
      }
    };

    initializeLanguage();
  }, [i18n, isAuthenticated, currentUser, hasReloaded, loading, hasCompleted]);

  // Show loading spinner while language is initializing or auth is loading
  // DISABLED: Language preference overlay temporarily disabled
  // if (!isLanguageReady || loading) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen bg-white">
  //       <div className="text-center">
  //         <LoadingSpinner size="large" text={loading ? 'Loading user preferences...' : 'Initializing language preferences...'} />
  //       </div>
  //     </div>
  //   );
  // }

  return children;
};

// Main content component that uses useLocation
const MainContent = () => {
  const location = useLocation();
  const { isAuthenticated, currentUser, logout } = useAuth();
  useLanguageSync();
  const isHomePage = location.pathname === '/';
  const shouldShowFooter = true;

  return (
    <div className="flex flex-col min-h-screen bg-white text-gray-900">
      <NavBar
        isAuthenticated={isAuthenticated}
        userData={currentUser}
        onLogout={logout}
      />
      <ScrollToTop />
      <div className="flex-1 flex flex-col">
        <main className={`flex-1 ${isHomePage ? 'pt-0' : 'pt-16'}`}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/titan" element={<Titan />} />
            {EVENT_ROUTES.map(({ path, Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
            <Route path="/cortex" element={<Cortex />} />
            <Route path="/jeltr" element={<Jeltr />} />
            <Route path="/archives" element={<Archives />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        {shouldShowFooter && (
          <div className="mt-auto">
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <LanguageInitializer>
          <MainContent />
        </LanguageInitializer>
      </Router>
    </AuthProvider>
  );
}

export default App;
